import React from "react";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";

export default function ScanJobBanner({ job }) {
  const s = job.summary || {};
  const isRunning = job.status === "running" || job.status === "queued";
  const isDone = job.status === "done";
  const isFailed = job.status === "failed";
  const phase = s.phase || "";
  // phase="" while the API actor runs; "complete" once listings are saved.
  const isPhase1 = isRunning && !phase;
  const isPhase1Done = phase === "complete" || isDone;

  return (
    <section
      className={`rounded-lg border p-4 ${
        isRunning
          ? "border-amber-200 bg-amber-50"
          : isDone
          ? "border-emerald-200 bg-emerald-50"
          : "border-red-200 bg-red-50"
      }`}
      data-testid="scan-job-banner"
    >
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        {isRunning ? (
          <Loader2 className="h-4 w-4 animate-spin text-amber-600" />
        ) : isDone ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
        ) : (
          <AlertTriangle className="h-4 w-4 text-red-600" />
        )}
        <span className="text-sm font-medium text-slate-900">
          {isFailed
            ? "Scan failed"
            : isDone
            ? "Scan complete"
            : isPhase1Done
            ? "Search done — saving listings…"
            : "Pulling open houses from Realtor.ca…"}
        </span>
        {s.oh_window?.start ? (
          <span className="text-xs text-slate-500" data-testid="scan-banner-window">
            {s.search_mode === "url" ? "Your link" : s.location} · {s.oh_window.start} → {s.oh_window.end}
          </span>
        ) : null}
        <span className="text-xs text-slate-500 ml-auto tabular-nums">
          Cost: ${(s.apify_cost_usd || 0).toFixed(4)} / ${(s.apify_usd_cap || 0).toFixed(2)}
        </span>
      </div>

      {/* Phase 1 results (always shown once scan finishes) */}
      <div className="mt-3">
        <div className="flex items-center gap-2 mb-2">
          <span className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full ${
            isPhase1Done
              ? "bg-emerald-100 text-emerald-800"
              : isPhase1
              ? "bg-amber-100 text-amber-800"
              : "bg-slate-100 text-slate-600"
          }`}>
            {isPhase1Done ? <CheckCircle2 className="h-3 w-3" /> : isPhase1 ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
            Search
          </span>
          {isPhase1Done && (
            <span className="text-xs text-slate-500">
              Found <span className="font-semibold text-slate-900 tabular-nums">{s.listings_found || 0}</span> listings
              {(s.results_total || 0) > (s.listings_found || 0) && (
                <> of <span className="font-semibold text-amber-700 tabular-nums">{s.results_total}</span> available — cut short by the cap</>
              )}
              {(s.listings_found || 0) > 0 && (s.awaiting_detail_pass || 0) > 0 && (
                <> · <span className="font-semibold text-emerald-700 tabular-nums">
                  {(s.listings_found || 0) - (s.awaiting_detail_pass || 0)}
                </span> with dates, <span className="font-semibold text-amber-700 tabular-nums">
                  {s.awaiting_detail_pass || 0}
                </span> without</>
              )}
              {(s.restored_from_bin || 0) > 0 && (
                <> · <span className="font-semibold text-sky-700 tabular-nums">{s.restored_from_bin}</span> restored from bin</>
              )}
            </span>
          )}
        </div>

        {(isPhase1Done || isPhase1) && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
            <Stat label="Available" value={s.results_total || s.listings_found || 0} />
            <Stat label="Listings" value={s.listings_found || 0} />
            <Stat label="New" value={s.new_added || 0} accent="emerald" />
            <Stat label="Updated" value={s.updated || 0} accent="sky" />
            <Stat label="Realtors" value={(s.matched_existing_realtors || 0) + (s.new_realtors_created || 0)} />
          </div>
        )}
      </div>

      {/* Errors */}
      {Array.isArray(job.errors) && job.errors.length > 0 ? (
        <details className="mt-3 text-xs">
          <summary className="cursor-pointer text-slate-600">Errors ({job.errors.length})</summary>
          <pre className="bg-white border border-slate-200 rounded p-2 mt-1 overflow-x-auto whitespace-pre-wrap">
{job.errors.join("\n")}
          </pre>
        </details>
      ) : null}
    </section>
  );
}

function Stat({ label, value, accent = "slate" }) {
  const cls = accent === "emerald" ? "text-emerald-700" : accent === "sky" ? "text-sky-700" : "text-slate-900";
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-lg font-bold tabular-nums ${cls}`}>{value}</div>
    </div>
  );
}
